import { createReducer, Reducer } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { RemoteResource } from "../common/fetchable";
import { EmptyFetchable, Fetchable } from "../common/fetchable/helpers";
import { WalletsActions } from "./actions";
import { WalletsDTO } from "./models/dto";
import { BrowserWallet } from "./models/internal";

export interface Wallets {
  browserWallet: BrowserWallet;
  walletsSummary: Fetchable<WalletsDTO.Responses.GetWalletsSummary.T>;
  totalBalance: Fetchable<WalletsDTO.Responses.GetTotalBalance.T>;
  wallets: {
    data: Fetchable<WalletsDTO.Responses.GetAll.T>;
    pagination: Pageable.T<keyof WalletsDTO.Responses.GetSingle.T>;
  };
  walletMap: Record<string, WalletsDTO.Responses.GetSingle.T | "LOADING">;
}

const initialStore: Wallets = {
  browserWallet: { status: "unavailable" },
  wallets: {
    data: EmptyFetchable,
    pagination: {},
  },
  walletMap: {},
  walletsSummary: EmptyFetchable,
  totalBalance: EmptyFetchable,
};

export namespace Wallets {
  export const all: RemoteResource<
    WalletsDTO.Responses.GetAll.T,
    WalletsDTO.Responses.GetAll.T,
    Pageable.T<keyof WalletsDTO.Responses.GetSingle.T>
  > = {
    fetch: WalletsActions.setPagination,
    updateStore: WalletsActions.setWallets,
    selector: (store) => store.wallets.wallets.data,
  };

  export const walletsSummary: RemoteResource<
    WalletsDTO.Responses.GetWalletsSummary.T,
    WalletsDTO.Responses.GetWalletsSummary.T
  > = {
    fetch: WalletsActions.getWalletsSummary,
    updateStore: WalletsActions.setWalletsSummary,
    selector: (store) => store.wallets.walletsSummary,
  };

  export const totalBalance: RemoteResource<
    WalletsDTO.Responses.GetTotalBalance.T,
    WalletsDTO.Responses.GetTotalBalance.T
  > = {
    fetch: WalletsActions.getTotalBalance,
    updateStore: WalletsActions.setTotalBalance,
    selector: (store) => store.wallets.totalBalance,
  };
}

// Reducer
export const walletsReducer: Reducer<
  Wallets,
  WalletsActions.Actions
> = createReducer<Wallets>(initialStore, (builder) => {
  builder
    .addCase(WalletsActions.setWallets, (state, action) => {
      state.wallets.data = action.payload;
      if (action.payload.status === "ready") {
        action.payload.data.content.forEach((p) => {
          state.walletMap[p.id] = p;
        });
      }
    })
    .addCase(WalletsActions.resetWallets, (state) => {
      state.wallets.data = EmptyFetchable;
      state.wallets.pagination = {};
      state.walletMap = {};
    })
    .addCase(WalletsActions.setSingleWallet, (state, action) => {
      state.walletMap[action.payload.id] = action.payload.result;
    })
    .addCase(WalletsActions.getWallets, (state) => {
      state.wallets.data = {
        status: "loading",
      };
    })
    .addCase(WalletsActions.setPagination, (state, action) => {
      state.wallets.pagination = action.payload;
    })
    .addCase(WalletsActions.resetWalletsSummary, (state) => {
      state.walletsSummary = EmptyFetchable;
    })
    .addCase(WalletsActions.getWalletsSummary, (state) => {
      state.walletsSummary = {
        status: "loading",
      };
    })
    .addCase(WalletsActions.setWalletsSummary, (state, action) => {
      state.walletsSummary = action.payload;
    })
    .addCase(WalletsActions.resetTotalBalance, (state) => {
      state.totalBalance = EmptyFetchable;
    })
    .addCase(WalletsActions.getTotalBalance, (state) => {
      state.totalBalance = {
        status: "loading",
      };
    })
    .addCase(WalletsActions.setTotalBalance, (state, action) => {
      state.totalBalance = action.payload;
    });
});
